/**
 * CoatingTermsBlock custom element.
 *
 * Handles:
 *  - Show/hide a mandatory confirmation checkbox when a watched coating option value
 *    (e.g. "Unbeschichtet") is selected.
 *  - Disables the Add-to-Cart button until the checkbox is ticked.
 *  - Enables/disables a hidden line item property so the confirmation is only saved
 *    when relevant.
 *  - Reacts to variant:update theme events (Section Rendering API).
 *
 * Cooperates with custom-width.js: both gate the same submit button using their own
 * data flag (data-coatingTermsDisabled / data-customWidthDisabled). The button is only
 * re-enabled when no gate flag remains.
 */
class CoatingTermsBlock extends HTMLElement {
  /** @type {AbortController|null} */
  #listenerController = null;

  connectedCallback() {
    this.#listenerController = new AbortController();
    this.#applyState();
    this.#attachListeners();
  }

  disconnectedCallback() {
    this.#listenerController?.abort();
    this.#listenerController = null;
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  get #isActive() {
    return this.dataset.show === 'true';
  }

  get #formId() {
    return this.dataset.productFormId;
  }

  get #checkbox() {
    return this.querySelector('[data-coating-terms-checkbox]');
  }

  get #property() {
    return this.querySelector('[data-coating-terms-property]');
  }

  get #submitButton() {
    const form = this.#formId
      ? document.getElementById(this.#formId) ?? document.querySelector(`[id="${this.#formId}"]`)
      : null;
    if (form) return form.querySelector('[type="submit"]');
    return document.querySelector('[data-type="add-to-cart-form"] [type="submit"]');
  }

  #isConfirmed() {
    if (!this.#isActive) return true;
    const checkbox = this.#checkbox;
    return !!(checkbox && checkbox.checked);
  }

  #validateAndToggleAtc() {
    const btn = this.#submitButton;
    if (!btn) return;

    if (this.#isConfirmed()) {
      if (btn.dataset.coatingTermsDisabled) {
        delete btn.dataset.coatingTermsDisabled;
        // Only re-enable if no other gate (e.g. custom width) still blocks it.
        if (!btn.dataset.customWidthDisabled) btn.removeAttribute('disabled');
      }
    } else {
      btn.setAttribute('disabled', '');
      btn.dataset.coatingTermsDisabled = '1';
    }
  }

  /** Only submit the confirmation property when active and confirmed. */
  #syncProperty() {
    const prop = this.#property;
    if (!prop) return;
    prop.disabled = !(this.#isActive && this.#isConfirmed());
  }

  #applyState() {
    this.hidden = !this.#isActive;

    if (!this.#isActive) {
      const checkbox = this.#checkbox;
      if (checkbox) checkbox.checked = false;
    }

    this.#validateAndToggleAtc();
    this.#syncProperty();
  }

  // ─── Event listeners ────────────────────────────────────────────────────────

  #attachListeners() {
    const signal = this.#listenerController.signal;

    const checkbox = this.#checkbox;
    if (checkbox) {
      checkbox.addEventListener(
        'change',
        () => {
          this.#validateAndToggleAtc();
          this.#syncProperty();
        },
        { signal }
      );
    }

    const scope = this.closest('.shopify-section, dialog') || document;
    scope.addEventListener(
      'variant:update',
      (event) => this.#handleVariantUpdate(event),
      { signal }
    );
  }

  #handleVariantUpdate(event) {
    const html = event.detail?.data?.html;
    if (html) {
      const updated = html.querySelector('coating-terms-block');
      this.dataset.show = updated ? updated.dataset.show : 'false';
    }
    this.#applyState();
  }
}

customElements.define('coating-terms-block', CoatingTermsBlock);
